/**
 * One status set everywhere (web and phone; docs/redesign-2026-11/README.md "Decisions"):
 *   Checking · Accepted +pts · Practice (reason) · Needs a fix · Not accepted ("Didn't pass our checks", never why)
 * From a platform contribution (`ContributionStatus`), a sim run (`pending | validating | accepted | rejected`), or a
 * value already in the shared set. Withdrawn work is hidden. Pure: no client, no React, so the phone can import it.
 *
 * A status this client doesn't know (the server added one after this build) is shown, never hidden
 * (docs/redesign-2026-11/resilience.md §1): `{key: 'other', label, tone: 'neutral'}` with the server's `status_label`,
 * else the key in words (`on_hold` → "On hold"). Only `withdrawn` and an empty status stay hidden.
 */

export type StatusKey = 'checking' | 'accepted' | 'practice' | 'fix' | 'rejected' | 'other'
/** The chip colours: `chk` wash, `ok` green, `prac` base blue, `fix` amber, `no` red, `neutral` plain (unknown). */
export type StatusTone = 'chk' | 'ok' | 'prac' | 'fix' | 'no' | 'neutral'

export interface Status {
  key: StatusKey
  /** The chip: "Checking", "Accepted +10", "Practice · first run", "Needs a fix", "Not accepted". */
  label: string
  tone: StatusTone
  /** Points it earned (accepted, not practice); null otherwise. */
  points: number | null
  /** Practice: the reason in words ("first run"); null for the others. */
  reason: string | null
  /** The longer line a result page shows next to the chip. */
  line: string
}

/** Anything with a status: a contribution, a sim run, a notification's data. */
export interface HasStatus {
  status: string | null | undefined
  practice?: boolean | null
  practice_reason?: string | null
  points?: number | null
  /** The server's words for the status; used when this client doesn't know the status. */
  status_label?: string | null
}

/** A server key in words: `on_hold` → "On hold", `needs-more.data` → "Needs more data". Empty stays empty. */
export function humanize(key: string | null | undefined): string {
  const words = String(key ?? '')
    .replace(/[_\-.]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  return words ? words[0]!.toUpperCase() + words.slice(1) : ''
}

/** The words for a server value: the server's own label, else this client's table, else the key in words. */
export function named(table: Readonly<Record<string, string>>, key: string | null | undefined, serverLabel?: string | null): string {
  if (serverLabel && serverLabel.trim()) return serverLabel.trim()
  if (key == null || key === '') return ''
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key]! : humanize(key)
}

const CHECKING = new Set(['submitted', 'processing', 'needs_review', 'pending', 'validating', 'queued', 'checking', 'in_review'])
const ACCEPTED = new Set(['accepted', 'partially_accepted'])
const FIX = new Set(['needs_correction', 'fix', 'needs_fix', 'needs_labels'])
const REJECTED = new Set(['rejected', 'not_accepted', 'failed'])

/** Practice reasons in words; anything else is shown as sent (servers send plain words for new reasons). */
export const PRACTICE_REASON: Record<string, string> = {
  first_run: 'first run',
  dev_build: 'dev build',
  device_not_verified: 'device not verified',
  unverified_device: 'device not verified',
  unverified: 'device not verified',
  practice_task: 'practice task',
}

export function practiceReason(r: string | null | undefined): string | null {
  if (!r) return null
  return PRACTICE_REASON[r] ?? r.replace(/_/g, ' ')
}

/** The shared status of a contribution or run; null only when it should be hidden (withdrawn, empty). A status this
 * client doesn't know is `other`, labelled with the server's `status_label` or the key in words. */
export function statusOf(x: HasStatus | null | undefined): Status | null {
  const s = (x?.status ?? '').toLowerCase()
  if (!s || s === 'withdrawn') return null
  if (CHECKING.has(s)) return { key: 'checking', label: 'Checking', tone: 'chk', points: null, reason: null, line: 'Checking' }
  if (FIX.has(s)) return { key: 'fix', label: 'Needs a fix', tone: 'fix', points: null, reason: null, line: 'Needs a fix' }
  if (REJECTED.has(s)) return { key: 'rejected', label: 'Not accepted', tone: 'no', points: null, reason: null, line: 'Not accepted · didn’t pass our checks' }
  if (s === 'practice' || (ACCEPTED.has(s) && x?.practice)) {
    const reason = practiceReason(x?.practice_reason)
    return {
      key: 'practice',
      label: reason ? `Practice · ${reason}` : 'Practice',
      tone: 'prac',
      points: null,
      reason,
      line: reason ? `Practice · ${reason === 'first run' ? 'first run of this task' : reason}` : 'Practice',
    }
  }
  if (ACCEPTED.has(s)) {
    const pts = typeof x?.points === 'number' && x.points > 0 ? x.points : null
    return { key: 'accepted', label: pts ? `Accepted +${pts}` : 'Accepted', tone: 'ok', points: pts, reason: null, line: pts ? `Accepted +${pts} · added to this week` : 'Accepted' }
  }
  const label = x?.status_label?.trim() || humanize(x?.status)
  return { key: 'other', label, tone: 'neutral', points: null, reason: null, line: label }
}

/** Is this status one this client knows? (`other` is the catch-all for new server statuses.) */
export const isKnownStatus = (s: Status | null | undefined): boolean => !!s && s.key !== 'other'

/** Filter chips in this order (My runs, Recordings). `other` is listed only when some rows have it. */
export const STATUS_ORDER: readonly StatusKey[] = ['checking', 'accepted', 'practice', 'fix', 'rejected', 'other']

/** The chips a filter row shows: the known ones always, `other` only when there are such rows (or it is selected). */
export function statusChips(count: (k: StatusKey) => number, selected?: string | null): StatusKey[] {
  return STATUS_ORDER.filter((k) => k !== 'other' || count(k) > 0 || selected === 'other')
}

export const STATUS_NAME: Record<StatusKey, string> = {
  checking: 'Checking',
  accepted: 'Accepted',
  practice: 'Practice',
  fix: 'Needs a fix',
  rejected: 'Not accepted',
  other: 'Other',
}

/** What a Not accepted result says, always: never the reason (integrity checks stay generic). */
export const NOT_ACCEPTED_TEXT = 'Didn’t pass our checks'
